/*
================================================================================
File: src/pages/Settings/ContractOptions.js
Description: Form for setting default contract options and signature.
================================================================================
*/
import React, { useState } from "react";
import instance from "../../api/axios";
import PageContent from "../../components/common/PageContent";

// SVG Icons
const BuildingIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M4 4a2 2 0 012-2h6a1 1 0 01.707.293l2.828 2.829A1 1 0 0115 4v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 000 2h6a1 1 0 100-2H6z" />
  </svg>
);

const ContractOptions = ({ pulldata, user }) => {
  const [titleName, setTitleName] = useState(user.preferr_title_name);
  const [titleAddress, setTitleAddress] = useState(user.preferr_title_address);
  const [optionPeriod, setOptionPeriod] = useState(user.default_period_option);
  const [optionMoney, setOptionMoney] = useState(
    user.default_period_option_money
  );
  const [earnestMoney, setEarnestMoney] = useState(user.earnest_money);
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState(null);



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append("type", "contract");
    formData.append("owner", user.app_id);
    formData.append("title_name", titleName);
    formData.append("title_address", titleAddress);
    formData.append("option_period", optionPeriod);
    formData.append("option_money", optionMoney);
    formData.append("earnest_money", earnestMoney);


    try {
      await instance.post("/updateProfile.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error updating contract info: " + e.response.data.message);
        console.error(
          "Error updating contract info: ",
          e.response.data.message
        );
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setIsLoading(false);
            await pulldata(user?.app_id);
    }
  };

  return (
    <PageContent
      title="Profile Settings"
      subtitle="Contract Options"
      error={error}
    >
      <div className="p-8 space-y-6">
        {/* Hero Banner */}
        <div className="bg-amber-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-white/20 rounded-lg">
              <DocumentIcon />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Contract Defaults</h2>
              <p className="text-amber-100">These values are pre-filled when you generate a new contract.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section A: Title Company */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                <BuildingIcon />
              </div>
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Title Company</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="titleName"
                  className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200"
                >
                  Preferred Title Company Name
                </label>
                <input
                  type="text"
                  id="titleName"
                  value={titleName}
                  onChange={(e) => setTitleName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  placeholder="My Awesome Title Company"
                />
              </div>
              <div>
                <label
                  htmlFor="titleAddress"
                  className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200"
                >
                  Preferred Title Company Address
                </label>
                <input
                  type="text"
                  placeholder="123 Main St, Houston, TX, 76549"
                  id="titleAddress"
                  value={titleAddress}
                  onChange={(e) => setTitleAddress(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
            </div>
            </div>

            {/* Section B: Contract Terms */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                  <DocumentIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Contract Terms</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label
                    htmlFor="optionPeriod2"
                    className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200"
                  >
                    Contract Option Period
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="10"
                      id="optionPeriod2"
                      value={optionPeriod}
                      onChange={(e) => setOptionPeriod(e.target.value)}
                      className="w-full px-3 py-2 pr-12 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">
                      Days
                    </span>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="optionMoney2"
                    className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200"
                  >
                    Contract Option Money
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                      $
                    </span>
                    <input
                      type="number"
                      placeholder="10"
                      id="optionMoney2"
                      value={optionMoney}
                      onChange={(e) => setOptionMoney(e.target.value)}
                      className="w-full px-3 py-2 pl-7 pr-12 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">
                      .00
                    </span>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="earnestMoney2"
                    className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200"
                  >
                    Contract Earnest Money
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      placeholder="100"
                      id="earnestMoney2"
                      value={earnestMoney}
                      onChange={(e) => setEarnestMoney(e.target.value)}
                      className="w-full px-3 py-2 pl-7 pr-12 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">
                      .00
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
        </form>
      </div>
    </PageContent>
  );
};

export default ContractOptions;
